import pureDayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import type { Dayjs } from "dayjs";
import { dayjs } from "@bigbinary/neeto-commons-frontend/utils";

import type { TimeValue } from "./types";

pureDayjs.extend(customParseFormat);

export const generateRange = (
  start: number,
  end: number,
  step = 1
): number[] => {
  const result: number[] = [];
  for (let i = start; i <= end; i += step) {
    result.push(i);
  }

  return result;
};

export const to12Hour = (
  hour24: number
): { hour12: number; period: "AM" | "PM" } => {
  const period = hour24 >= 12 ? "PM" : "AM";
  const hour12 = hour24 % 12 || 12;

  return { hour12, period };
};

export const to24Hour = (hour12: number, period: "AM" | "PM"): number => {
  if (period === "AM") {
    return hour12 === 12 ? 0 : hour12;
  }

  return hour12 === 12 ? 12 : hour12 + 12;
};

export const dateToTimeValue = (date: Date): TimeValue => ({
  hours: date.getHours(),
  minutes: date.getMinutes(),
  seconds: date.getSeconds(),
});

export const coerceToTimeValue = (
  value: TimeValue | Date | Dayjs | null | undefined
): TimeValue | null => {
  if (!value) return null;
  if (value instanceof Date) return dateToTimeValue(value);

  const dayjsLike = value as {
    toDate?: () => Date;
    hour?: () => number;
    minute?: () => number;
    second?: () => number;
  };

  if (typeof dayjsLike.hour === "function") {
    return {
      hours: dayjsLike.hour!(),
      minutes: dayjsLike.minute!(),
      seconds: dayjsLike.second?.() ?? 0,
    };
  }

  if (typeof dayjsLike.toDate === "function") {
    return dateToTimeValue(dayjsLike.toDate());
  }

  const timeValue = value as TimeValue;

  return {
    hours: timeValue.hours,
    minutes: timeValue.minutes,
    seconds: timeValue.seconds ?? 0,
  };
};

export const timeValueEquals = (
  a: TimeValue | null,
  b: TimeValue | null
): boolean => {
  if (a === null && b === null) return true;
  if (!a || !b) return false;

  return (
    a.hours === b.hours &&
    a.minutes === b.minutes &&
    (a.seconds ?? 0) === (b.seconds ?? 0)
  );
};

export const getDefaultTimeMaskFormat = (
  format: "12" | "24",
  showSeconds: boolean
): string => {
  if (format === "12") return showSeconds ? "hh:mm:ss AA" : "hh:mm AA";

  return showSeconds ? "HH:mm:ss" : "HH:mm";
};

export const getTimePlaceholder = (
  format: "12" | "24",
  showSeconds: boolean,
  displayFormat?: string
): string => displayFormat ?? getDefaultTimeMaskFormat(format, showSeconds);

export const padNumber = (num: number, length = 2): string =>
  String(num).padStart(length, "0");

export const formatTimeDisplay = (
  time: TimeValue | null,
  format: "12" | "24",
  showSeconds: boolean
): string => {
  if (!time) return "";

  if (format === "12") {
    const { hour12, period } = to12Hour(time.hours);
    const parts = [padNumber(hour12), padNumber(time.minutes)];
    if (showSeconds) parts.push(padNumber(time.seconds ?? 0));

    return `${parts.join(":")} ${period}`;
  }

  const parts = [padNumber(time.hours), padNumber(time.minutes)];
  if (showSeconds) parts.push(padNumber(time.seconds ?? 0));

  return parts.join(":");
};

export const parseTimeString = (
  input: string,
  format: "12" | "24"
): TimeValue | null => {
  const trimmed = input.trim();
  if (!trimmed) return null;

  if (format === "12") {
    const match = trimmed.match(
      /^(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?\s*(AM|PM)$/i
    );
    if (!match) return null;

    const hour12 = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const seconds = match[3] ? parseInt(match[3], 10) : 0;
    const period = match[4].toUpperCase() as "AM" | "PM";

    if (hour12 < 1 || hour12 > 12 || minutes > 59 || seconds > 59) return null;

    return { hours: to24Hour(hour12, period), minutes, seconds };
  }

  const match = trimmed.match(/^(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?$/);
  if (!match) return null;

  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const seconds = match[3] ? parseInt(match[3], 10) : 0;

  if (hours > 23 || minutes > 59 || seconds > 59) return null;

  return { hours, minutes, seconds };
};

/**
 * Format a TimeValue using a dayjs-style format string (e.g. "h:mm A").
 */
export const formatTimeWithPattern = (
  time: TimeValue | null,
  pattern: string
): string => {
  if (!time) return "";

  return pureDayjs("2000-01-01T00:00:00")
    .hour(time.hours)
    .minute(time.minutes)
    .second(time.seconds ?? 0)
    .format(pattern);
};

/**
 * Parse a time string against a dayjs-style format pattern.
 * Returns null if the string does not match the pattern.
 */
export const parseTimeWithPattern = (
  input: string,
  pattern: string
): TimeValue | null => {
  const trimmed = input.trim();
  if (!trimmed) return null;

  const parsed = pureDayjs(trimmed, pattern, true);
  if (!parsed.isValid()) return null;

  return {
    hours: parsed.hour(),
    minutes: parsed.minute(),
    seconds: parsed.second(),
  };
};

/**
 * Convert TimeValue to a timezone-aware Dayjs.
 * Formats in browser-local time, then reparses through timezone-aware dayjs.
 */
export const timeValueToDayjs = (time: TimeValue): Dayjs => {
  const h = padNumber(time.hours);
  const m = padNumber(time.minutes);
  const s = padNumber(time.seconds ?? 0);
  const today = dayjs(new Date()).format("YYYY-MM-DD");

  return dayjs(`${today} ${h}:${m}:${s}`);
};
